/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { AnyObject } from '@triare/auth-redux';
import listSaga from './list';
import byIdSaga from './byId';
import createSaga, { TicketCreateData } from './create';
import updateSaga, { TicketUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { PaginatedResponse } from '../types';
import { User } from '../auth';
import { capitalizeFirstLetter } from '../../utils';

export const moduleName = 'ticket';

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in progress',
  RESOLVED = 'resolved',
}

export const TicketStatusTitle: Record<TicketStatus, string> = {
  [TicketStatus.OPEN]: capitalizeFirstLetter(TicketStatus.OPEN),
  [TicketStatus.IN_PROGRESS]: capitalizeFirstLetter(TicketStatus.IN_PROGRESS),
  [TicketStatus.RESOLVED]: 'Resolve',
};

export enum TicketType {
  SUGGESTION = 'suggestion',
  FEEDBACK = 'feedback',
  COMPLAINT = 'complaint',
  BUG = 'bug',
  REPORT = 'report',
  OTHER = 'other',
  NEW_CATEGORY = 'new category',
  NEW_SUBCATEGORY = 'new subcategory',
  EXPECTED_TIP = 'expected tip',
}

export interface Ticket {
  id: string;
  ticketId: number;
  description: string;
  type: TicketType;
  status: TicketStatus;
  user?: User;
  details: AnyObject;
  createdAt: string;
  updatedAt: string;
}

export interface State {
  list: BaseState<PaginatedResponse<Ticket>>;
  byId: BaseState<Ticket>;
  create: BaseState<TicketCreateData>;
  update: BaseState<TicketUpdateData>;
  delete: BaseState<{ id: string }>;
}

const models = ['list', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const ticket = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = ticket;

export function useTicket(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
